import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import NotificationSnackbar, { NotificationType } from 'components/common/NotificationSnackBar';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../store';
import { fetchPublishers } from 'features/publishers/slices/publishersSlice';
import PublisherFilters from 'features/publishers/components/common/PublisherFilters';
import PublishersTable from 'features/publishers/components/common/PublishersTable';
import WebsiteForm from 'features/websites/components/WebsiteForm';
import { Publisher } from 'types/publisher.types';

const PublishersPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.publishers);
  
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    type: 'info' as NotificationType,
  });

  const [openWebsiteForm, setOpenWebsiteForm] = useState(false);
  const [newPublisherForWebsite, setNewPublisherForWebsite] = useState<Publisher | undefined>(undefined);

  useEffect(() => {
    const loadPublishers = async () => {
      try {
        await dispatch(fetchPublishers());
      } catch (err) {
        setNotification({
          open: true,
          message: 'Failed to load publishers',
          type: 'error',
        });
      }
    };
    
    loadPublishers();
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      setNotification({
        open: true,
        message: error,
        type: 'error',
      });
    }
  }, [error]);

  const closeNotification = () => {
    setNotification({
      ...notification,
      open: false,
    });
  };

  const handlePublisherCreated = (publisher: Publisher) => {
    // Open WebsiteForm with defaults from created publisher
    setNewPublisherForWebsite(publisher);
    setOpenWebsiteForm(true);
  };

  const handleCloseWebsiteForm = () => {
    setOpenWebsiteForm(false);
    setNewPublisherForWebsite(undefined);
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Publisher Management
      </Typography>
      <Typography variant="body1" paragraph>
        Manage publishers in the Location Station platform. You can add new publishers, edit existing ones,
        and manage their associated websites and journalists.
      </Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <PublisherFilters />
      </Paper>
      
      {/* Show spinner but keep table mounted to avoid unmounting state during create/update */}
      {isLoading && !error && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
          <CircularProgress size={24} />
        </Box>
      )}
      
      <PublishersTable onPublisherCreated={handlePublisherCreated} />

      {newPublisherForWebsite && (
        <WebsiteForm
          open={openWebsiteForm}
          onClose={handleCloseWebsiteForm}
          publisherId={newPublisherForWebsite.id}
          initialTab={0}
        />
      )}

      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        type={notification.type}
        onClose={closeNotification}
      />
    </Box>
  );
};

export default PublishersPage;